import * as Sentry from '@sentry/angular';
import { AuthService } from '../../auth/auth.service';

interface ErrorContext {
  component: string;
  operation: string;
  extraData?: Record<string, any>;
}

interface HttpError {
  status?: number;
  statusText?: string;
  url?: string;
  error?: {
    error?: {
      code?: number;
      internalErrorCode?: string;
      message?: string;
      translatedMessage?: string;
    };
  };
  message?: string;
}

/**
 * Centralized error capture utility for Sentry error reporting
 * Automatically extracts HTTP error details and adds contextual information
 *
 * @param error - The error object to capture
 * @param context - Additional context about where the error occurred
 *
 * @example
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   captureError(error, {
 *     component: 'form-details',
 *     operation: 'loadForm',
 *     extraData: { formId: this.formId }
 *   });
 * }
 */
export function captureError(error: any, context: ErrorContext): void {
  const sentryContext: any = {
    tags: {
      component: context.component,
      operation: context.operation,
    },
    extra: {}
  };

  // Extract HTTP error details if available
  if (isHttpError(error)) {
    const httpError = error as HttpError;

    sentryContext.tags.httpStatus = httpError.status || 'unknown';

    sentryContext.extra = {
      ...sentryContext.extra,
      url: httpError.url,
      statusText: httpError.statusText,
      errorCode: httpError.error?.error?.code,
      internalErrorCode: httpError.error?.error?.internalErrorCode,
      errorMessage: httpError.error?.error?.message,
      translatedMessage: httpError.error?.error?.translatedMessage,
      httpMessage: httpError.message,
    };
  }

  // Add any additional context data
  if (context.extraData) {
    sentryContext.extra = {
      ...sentryContext.extra,
      ...context.extraData
    };
  }

  // Clean up undefined values from the extra object
  Object.keys(sentryContext.extra).forEach(key => {
    if (sentryContext.extra[key] === undefined) {
      delete sentryContext.extra[key];
    }
  });

  // Capture the exception with full context
  Sentry.captureException(error, sentryContext);
}

/**
 * Log a Sentry 'info' for action tracking
 */
export function logInfoAction(source: string, data?: Record<string, any>): void {
  Sentry.logger.info(source, { log_source: data })
}

/**
 * Log a Sentry 'warning' for action tracking
 */
export function logWarningAction(source: string, data?: Record<string, any>): void {
  Sentry.logger.warn(source, { log_source: data })
}

/**
 * Log a Sentry 'error' for action tracking
 */
export function logErrorAction(source: string, data?: Record<string, any>): void {
  Sentry.logger.error(source, { log_source: data })
}

/**
 * Helper function to check if an error is an HTTP error
 */
function isHttpError(error: any): boolean {
  return error && (
    typeof error.status === 'number' ||
    typeof error.url === 'string' ||
    error.name === 'HttpErrorResponse'
  );
}